import { Toast } from '@base-ui/react/toast'

import { errorMessage } from './errors'

export type ToastTone = 'success' | 'error' | 'info'

/**
 * Global toast manager.
 *
 * Lives outside React so query hooks, mutation `onError` handlers, and plain
 * async helpers can all report to the user without being passed a hook. The
 * provider in `ToastRegion` binds this instance to the rendered viewport.
 */
export const toastManager = Toast.createToastManager()

interface ToastOptions {
  id?: string
  description?: string
  action?: { label: string; onClick: () => void }
  /** ms before auto-dismiss. 0 keeps it up until dismissed. */
  timeout?: number
}

function add(tone: ToastTone, title: string, options: ToastOptions = {}) {
  return toastManager.add({
    id: options.id,
    title,
    type: tone,
    description: options.description,
    actionProps: options.action ? { children: options.action.label, onClick: options.action.onClick } : undefined,
    // Errors stay until dismissed — they usually carry a reason worth reading,
    // and a failure that vanishes on its own is the problem we're fixing.
    timeout: options.timeout ?? (tone === 'error' ? 0 : 4000),
    priority: tone === 'error' ? 'high' : 'low'
  })
}

export const toast = {
  success: (title: string, options?: ToastOptions) => add('success', title, options),
  error: (title: string, options?: ToastOptions) => add('error', title, options),
  info: (title: string, options?: ToastOptions) => add('info', title, options),
  close: (id: string) => toastManager.close(id)
}

/**
 * Pulls a readable message off whatever a failed request threw.
 * Keeps `onError` handlers to a single line.
 */
export function toErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  return errorMessage(error, fallback)
}
